import { batch, connect } from 'react-redux';
import { sendGAEvent } from '@noths/polaris-client-google-analytics';
import { selectIsFeatureFlagVariation } from '@noths/polaris-client-user-configuration';

import { actions as filterMenuActions } from 'src/components/organisms/FilterMenu/modules/slice';
import { SORT_FILTER_UID } from 'src/constants/elementIds';
import { FacetedSearch } from 'src/constants/featureFlags';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import type { AppThunkDispatch } from 'src/redux/store';
import type { Filter, SelectedFilterOption, SortOptionValue } from 'src/types';
import {
  updateFilters,
  UpdateFiltersActionType,
} from '../../../../redux/products/thunks/updateFilters';
import { FilterOptions } from '../components/FilterOptions';
import {
  selectTotalFiltersAppliedCount,
  selectTotalSelectedFilterOptionsCount,
} from '../modules/selectors';
import { getFilterOptionsWithoutNullActiveCounts } from '../modules/selectors/selectorHelpers';
import { actions as filterActions } from '../modules/slice';
import type {
  FilterOptionsContainerDispatchProps,
  FilterOptionsContainerStateProps,
} from './FilterOptionsContainer.types';

export const mapStateToProps = (
  state: ReduxApplicationState,
  ownProps: { filter: Filter; isExpanded?: boolean },
): FilterOptionsContainerStateProps => {
  const isFacetedSearchVariant = selectIsFeatureFlagVariation(
    state,
    FacetedSearch.NAME,
    FacetedSearch.VARIANT_FACETED_SEARCH,
  );

  const filterWithoutActiveCounts = {
    ...ownProps.filter,
    options: getFilterOptionsWithoutNullActiveCounts(ownProps.filter.options),
  };

  return {
    currencyCode: state.products.currencyCode,
    filter: isFacetedSearchVariant ? ownProps.filter : filterWithoutActiveCounts,
    isExpanded: ownProps.isExpanded,
    selectedFilterOptions: state.filter.collections.selectedFilterOptions,
    totalFiltersAppliedCount: selectTotalFiltersAppliedCount(state),
    totalSelectedFilterOptionsCount: selectTotalSelectedFilterOptionsCount(state),
  };
};

export const mapDispatchToProps = (
  dispatch: AppThunkDispatch,
): FilterOptionsContainerDispatchProps => ({
  onSelectFilterOption: (selectedFilterOption: SelectedFilterOption) => {
    batch(() => {
      const sortOptionSelected = selectedFilterOption.filterUid === SORT_FILTER_UID;

      if (sortOptionSelected) {
        /*
          When sort options are being used as filter options we need to ensure the sorting options state keeps in sync with the sort filter.
          We need to do that as the submission logic in the thunk looks at the original sorting options when sending params to the API and
          also keeps the UI in sync if the user suddenly goes to a desktop view and sees the original sort drop down (since this feature we are doing is only for MW & TA).
         */
        dispatch(
          filterMenuActions.selectSortOption(
            selectedFilterOption.filterOptionValue as SortOptionValue,
          ),
        );
      }

      dispatch(filterActions.selectFilterOption(selectedFilterOption));
      dispatch(
        updateFilters(
          sortOptionSelected ? UpdateFiltersActionType.SORT : UpdateFiltersActionType.FILTER,
        ),
      );
    });
  },
  onExpandableContentToggle: ({
    isExpanded,
    filterTitle,
    totalFiltersAppliedCount,
    totalSelectedFilterOptionsCount,
  }) => {
    sendGAEvent({
      event: 'custom_event',
      event_category: 'Filters',
      event_action: `show ${isExpanded ? 'less' : 'all'} ${filterTitle}`,
      event_label: `number of categories: ${totalFiltersAppliedCount}|number of filters: ${totalSelectedFilterOptionsCount}`,
    });
  },
});

export const FilterOptionsContainer = connect(mapStateToProps, mapDispatchToProps)(FilterOptions);
